/**
 * Compiles the ~4.6 MB Piezo WASM binary once and shares it with every consumer. A WebAssembly.Module is
 * structured-cloneable, so the one compile can be posted to every worker. Best-effort throughout: any
 * failure degrades to each consumer compiling its own copy.
 *
 * This leans on two things about the SDK, so it is worth knowing what breaks them:
 *
 * 1. Its wasm-bindgen glue skips compiling when a binary is already loaded (`if (wasm !== undefined)`).
 *    If a future generator drops that guard, adoptSharedPiezoWasm still succeeds and the SDK compiles
 *    anyway.
 * 2. Our import must resolve to the SDK's OWN copy of the package, because initSync writes to state
 *    inside it. That holds today because package.json asks for `*` and Config deliberately does not
 *    declare it, so we inherit whatever PiezoPlenumTypescript brought in. Pinning a version could give
 *    us a second copy, and then we would seed ours while the SDK reads its own.
 *
 * Either way the failure is a SILENT loss of the optimisation, never a broken signature -- the worst case
 * is what happened before this existed. A rename of the .wasm asset fails the webpack build instead, and
 * a changed initSync signature fails tsc, so only the two cases above are quiet.
 */

// The same module instance the Piezo SDK imports, which is what makes adoptSharedPiezoWasm work.
import { initSync } from '@amzn/amzn-piezo-plenum-wasm';
import { PiezoDebugPrefix } from '../provisioning/PiezoClient.constants';
import { getPiezoWasmUrl } from './url';

/** Memoised so N consumers share one compile. Dropped again on failure. */
let compilePromise: Promise<WebAssembly.Module | undefined> | undefined;

/** The shared module, or `undefined` if it could not be produced. Never rejects. */
export function getSharedPiezoWasmModule(): Promise<WebAssembly.Module | undefined> {
  if (!compilePromise) {
    // Dropped on failure so a later consumer retries rather than caching one transient blip.
    compilePromise = compileSharedModule().then((wasmModule) => {
      if (!wasmModule) {
        compilePromise = undefined;
      }
      return wasmModule;
    });
  }
  return compilePromise;
}

async function compileSharedModule(): Promise<WebAssembly.Module | undefined> {
  try {
    if (typeof WebAssembly === 'undefined' || typeof fetch !== 'function') {
      return undefined;
    }
    const url = getPiezoWasmUrl();
    const startedAt = Date.now();
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} fetching ${url}`);
    }
    // Buffered rather than compileStreaming: streaming rejects unless the response carries
    // Content-Type: application/wasm, which the static handler does not set.
    const wasmModule = await WebAssembly.compile(await response.arrayBuffer());
    console.info(
      `${PiezoDebugPrefix} compiled shared Piezo WASM module in ${Date.now() - startedAt}ms; ` +
        `sign workers will reuse it instead of compiling their own`,
    );
    return wasmModule;
  } catch (err) {
    // `warn`, not error: this degrades rather than breaking signing.
    console.warn(
      `${PiezoDebugPrefix} could not compile a shared Piezo WASM module (${String(err)}); ` +
        `each sign worker will compile its own copy.`,
    );
    return undefined;
  }
}

/** Memoised so the several ensureClients call sites share one adoption attempt. */
let adoptPromise: Promise<void> | undefined;

/**
 * Loads the shared binary on the main thread so PiezoPlenumClient.initialize() does not compile a second
 * copy: the SDK checks whether one is already loaded before compiling, so doing this first makes that step
 * a no-op. Never runs in a worker (the pool hands each worker its own copy). Best-effort, never rejects.
 */
export function adoptSharedPiezoWasm(): Promise<void> {
  if (!adoptPromise) {
    // Only a genuine failure is retryable; the worker skip is a permanent, correct no-op.
    adoptPromise = adoptOnMainThread().then((outcome) => {
      if (outcome === 'retry') {
        adoptPromise = undefined;
      }
    });
  }
  return adoptPromise;
}

/** Test-only: drop both memos so cases do not share one compile or one adoption. */
export function resetPiezoWasmModule(): void {
  compilePromise = undefined;
  adoptPromise = undefined;
}

async function adoptOnMainThread(): Promise<'adopted' | 'skipped' | 'retry'> {
  if (typeof (globalThis as { WorkerGlobalScope?: unknown }).WorkerGlobalScope !== 'undefined') {
    return 'skipped';
  }
  // Shares the memo above, so the pool's warm-up and this await the same single compile.
  const wasmModule = await getSharedPiezoWasmModule();
  if (!wasmModule) {
    return 'retry';
  }
  try {
    const startedAt = Date.now();
    initSync({ module: wasmModule });
    console.info(
      `${PiezoDebugPrefix} adopted the shared Piezo WASM module on the main thread in ` +
        `${Date.now() - startedAt}ms; the Piezo SDK will not compile its own copy`,
    );
    return 'adopted';
  } catch (err) {
    // `warn`, not error: this degrades rather than breaking signing.
    console.warn(
      `${PiezoDebugPrefix} could not adopt the shared Piezo WASM module on the main thread ` +
        `(${String(err)}); the Piezo SDK will compile its own copy.`,
    );
    return 'retry';
  }
}
